import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, ExternalLink } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, AnnotatedDocument, FaqList, faqSchema, ClosingCta, usePrimaryCta } from '../components/common/B2B';

// DeCA (documento de control) con contenido diseñado desde cero (nov-2026): el
// recorrido, el DeCA real anotado (varias entregas, sin firmas), el camino de
// crecimiento a otros documentos, preguntas frecuentes y marco normativo.
// Jesús (4-nov): fuera «la norma en ocho puntos» y «más que un PDF con un QR»
// (repetían el documento anotado, el recorrido y las preguntas) y fuera el
// énfasis en el 5 de octubre, que ya pasó.
// Afirmaciones legales: las que ya publicaba esta página (claves normativas,
// art. 6 de la Orden FOM/2861/2012, Resolución de 5-jun-2026, Ley 9/2025).
// Imágenes: renderizador real con datos ficticios
// (marketing/documentos-muestra/render-deca-clasico.mjs).
// Versión inglesa añadida el 4-nov-2026 (mismas afirmaciones legales; los artículos
// de «Para saber más» solo existen en español y no se muestran en inglés).

const PAGE_URL = { es: 'https://www.bluecmr.com/documentos/documento-control', en: 'https://www.bluecmr.com/en/documents/control-document' };

// Marcas sobre deca-clasico-completo.webp (en % de la imagen): QR, cargador
// contractual, transportista efectivo, vehículo, origen y fecha, mercancía,
// entregas e historial.
// En inglés, el DeCA pintado en inglés (deca-clasico-completo-en.webp) y las marcas
// trasladadas caja a caja desde el render español (4-nov-2026).
const MARKS = {
  es: [
    { x: 87.7, y: 7.0 }, { x: 27.9, y: 16.8 }, { x: 75.6, y: 16.8 }, { x: 75.6, y: 29.9 },
    { x: 29.1, y: 41.1 }, { x: 23.3, y: 50.1 }, { x: 88.6, y: 69.0 }, { x: 34.9, y: 89.0 },
  ],
  en: [
    { x: 87.7, y: 6.9 }, { x: 27.9, y: 16.6 }, { x: 75.6, y: 16.6 }, { x: 75.6, y: 29.5 },
    { x: 29.1, y: 40.5 }, { x: 23.3, y: 49.4 }, { x: 88.6, y: 69.4 }, { x: 34.9, y: 89.2 },
  ],
};
const DOC_IMG = {
  es: { hero: '/images/documentos/deca-clasico.webp', full: '/images/documentos/deca-clasico-completo.webp', h: 1081 },
  en: { hero: '/images/documentos/deca-clasico-en.webp', full: '/images/documentos/deca-clasico-completo-en.webp', h: 1096 },
};

const COPY = {
  es: {
    seoTitle: 'DeCA de transporte: el documento de control electrónico obligatorio | BlueCMR',
    seoDescription: 'The DeCA, Spain’s electronic control document, is mandatory for domestic road haulage in Spain: what the rules require and how to issue it with BlueCMR.',
    seoKeywords: 'deca, deca transporte, documento de control, documento electrónico de control administrativo, dca, documento de control obligatorio, orden fom/2861/2012, resolución 5 junio 2026',
    heroEyebrow: 'DeCA · Documento de control',
    heroTitle: 'DeCA de transporte: el documento de control, electrónico y listo para la inspección',
    heroText: 'Obligatorio en formato electrónico en el transporte nacional. Con BlueCMR se emite en menos de dos minutos, llega al conductor por el canal que prefiera y la inspección lo abre con un QR.',
    heroChecks: ['Orden FOM/2861/2012', 'Resolución de 5 de junio de 2026', 'Varias entregas en un DeCA'],
    heroAlt: 'DeCA de BlueCMR en diseño clásico, con varias entregas',
    platform: 'Conozca la plataforma',
    docEyebrow: 'El documento',
    docTitle: 'Así es un DeCA de BlueCMR',
    docText: 'Un reparto con tres entregas en un solo documento (datos de ejemplo), con los datos del artículo 6 de la Orden FOM/2861/2012.',
    docAlt: 'DeCA completo emitido con BlueCMR, con tres entregas y datos de ejemplo',
    notes: [
      { title: 'Código QR', text: 'El agente lo escanea y abre el documento al instante desde una dirección HTTPS estable.' },
      { title: 'Cargador contractual', text: 'Nombre, NIF y domicilio de quien contrata el transporte.' },
      { title: 'Transportista efectivo', text: 'Nombre y NIF de quien realiza el transporte.' },
      { title: 'Vehículo', text: 'Matrícula de la tractora y del remolque. Si cambia en ruta, se actualiza con la misma URL y el mismo QR.' },
      { title: 'Origen y fecha del transporte', text: 'Lugar de carga y fecha de realización del transporte.' },
      { title: 'Mercancía', text: 'Naturaleza y peso de la mercancía.' },
      { title: 'Entregas', text: 'Cada una con su destinatario, su mercancía y su resultado, en el mismo documento.' },
      { title: 'Historial', text: 'Cada confirmación de entrega o cambio queda registrado con fecha y hora.' },
    ],
    howEyebrow: 'Cómo funciona',
    howTitle: 'Del muelle a la última entrega',
    howText: 'El DeCA (Documento electrónico de Control Administrativo, antes DCA) es el documento de control de la Orden FOM/2861/2012 y debe ir a bordo en todo transporte de mercancías por carretera en territorio nacional. Así se gestiona con BlueCMR:',
    steps: [
      { place: 'Oficina', title: 'Emisión', text: 'En menos de dos minutos desde la web o la app, o automática desde su ERP o TMS por API.' },
      { place: 'Conductor', title: 'Envío', text: 'Le llega por correo, WhatsApp, SMS o enlace, sin instalar nada, con constancia de entrega y de lectura.' },
      { place: 'En ruta', title: 'Inspección', text: 'El agente escanea el QR y ve el documento al instante; el conductor lo muestra aunque no haya cobertura.' },
      { place: 'Destino', title: 'Entregas', text: 'Cada entrega puede confirmarse en el propio DeCA, con su resultado y la hora.' },
      { place: 'Después', title: 'Archivo', text: 'El documento se conserva con todos sus cambios y confirmaciones, con los datos estructurados para eFTI.' },
    ],
    growEyebrow: 'Más allá del DeCA',
    growTitle: 'Empiece por el DeCA, crezca cuando quiera',
    growText: 'Con los mismos datos y la misma plataforma puede pasar a documentos con más funciones sin cambiar de herramienta.',
    grow: [
      { title: 'Carta de porte + DeCA', text: 'Contrato y documento de control en uno, con firmas.', to: '/documentos/carta-porte' },
      { title: 'Documento de transporte ADR + DeCA', text: 'Mercancías peligrosas con los datos del documento de control.', to: '/documentos/transporte-adr' },
      { title: 'Albarán + DeCA', text: 'El albarán de entrega del cargador, con función de documento de control.' },
      { title: 'eCMR', text: 'Para el transporte internacional, con la misma operativa.', to: '/documentos/ecmr' },
      { title: 'Integración con su ERP o TMS', text: 'Que los DeCA salgan solos de su sistema, por API o fichero.', to: '/interoperabilidad' },
    ],
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Dudas habituales sobre el DeCA',
    faq: [
      { q: '¿Es lo mismo que el DCA o el documento de control?', a: 'Sí. DeCA (Documento electrónico de Control Administrativo) es la denominación de la Resolución de 5 de junio de 2026 para el documento de control de la Orden FOM/2861/2012, que antes se conocía como DCA o documento de control.' },
      { q: '¿Quién tiene que emitirlo?', a: 'El cargador contractual lo formaliza y el transportista efectivo lo formaliza y lo lleva a bordo. Los dos responden de que exista y esté bien cumplimentado.' },
      { q: '¿Hay que firmarlo?', a: 'No. La firma no es requisito de validez del DeCA. Si se firma, debe ser una firma electrónica avanzada o cualificada. Con BlueCMR puede añadir una firma biométrica avanzada cuando el DeCA hace también de prueba de entrega.' },
      { q: '¿Qué ocurre si subcontrato el transporte?', a: 'Quien subcontrata pasa a ser el cargador contractual del nuevo envío: figura como tal en el DeCA y aporta sus datos al transportista efectivo.' },
      { q: '¿Puedo usar un solo DeCA para varias entregas?', a: 'Sí, cuando las entregas comparten cargador contractual y transportista efectivo, identificando cada envío dentro del documento, como permite la Resolución de 5 de junio de 2026.' },
      { q: '¿Sirve otro documento como DeCA?', a: 'Sí. Un albarán o una carta de porte que contenga todos los datos del documento de control cumple también esa obligación (art. 2.2 de la Orden FOM/2861/2012). BlueCMR ofrece carta de porte, albarán y documento ADR con los datos del DeCA.' },
      { q: '¿Aplica al transporte internacional?', a: 'No. El transporte internacional se documenta con los convenios suscritos por España, como el CMR o el eCMR. El DeCA es del transporte interior: nacional y cabotaje.' },
      { q: '¿Hay transportes exentos?', a: 'Sí: el transporte privado complementario, que debe acreditar el art. 102.2 a), c) y d) de la LOTT, y la paquetería de bultos reducidos.' },
      { q: '¿Qué pasa si no se lleva?', a: 'No llevarlo, o llevarlo en papel o incompleto, se sanciona conforme al régimen sancionador de la LOTT.' },
    ],
    lawTitle: 'Marco normativo',
    law: [
      { title: 'Orden FOM/2861/2012', text: 'Documento de control en el transporte por carretera.', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2013-154' },
      { title: 'Ley 9/2025 de Movilidad Sostenible', text: 'Obligatoriedad del formato electrónico.', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545' },
      { title: 'Resolución de 5 de junio de 2026', text: 'Requisitos técnicos del DeCA (BOE-A-2026-12784).', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784' },
      { title: 'LOTT (Ley 16/1987)', text: 'Régimen sancionador (arts. 141 y 143).', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-1987-17803' },
      { title: 'Reglamento eIDAS (UE 910/2014)', text: 'Firma electrónica avanzada.', href: 'https://eur-lex.europa.eu/eli/reg/2014/910/oj' },
    ],
    resTitle: 'Para saber más',
    res: [
      { to: '/articulos/formato-pdfa1a-documento-control-digital-2026', title: 'Requisitos técnicos del DeCA', text: 'Lo que pide la Resolución de 5 de junio de 2026.' },
      { to: '/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr', title: 'DeCA, carta de porte y CMR', text: 'Qué documento corresponde a cada transporte.' },
    ],
    closing: 'Cumpla con el DeCA sin cambiar su operativa',
    closingText: 'Del primer documento a toda la flota, con la misma plataforma.',
    breadcrumb: ['Inicio', 'Documentos'],
  },
  en: {
    seoTitle: 'DeCA: Spain’s electronic control document for road haulage | BlueCMR',
    seoDescription: 'The DeCA, Spain’s electronic control document, is mandatory for domestic road haulage in Spain, cabotage included: what the rules require and how to issue it with BlueCMR.',
    seoKeywords: 'deca, deca spain, spanish control document, documento de control, electronic control document spain, dca, cabotage spain, order fom/2861/2012',
    heroEyebrow: 'DeCA · Control document',
    heroTitle: 'DeCA: Spain’s control document for road haulage, electronic and ready for inspection',
    heroText: 'The DeCA, Spain’s electronic control document (Documento electrónico de Control Administrativo), is mandatory for domestic road haulage in Spain, cabotage included. With BlueCMR you issue it in under two minutes, it reaches the driver through the channel of your choice and inspectors open it with a QR code.',
    heroChecks: ['Order FOM/2861/2012', 'Resolution of 5 June 2026', 'Several deliveries in one DeCA'],
    heroAlt: 'BlueCMR DeCA in classic layout, with several deliveries',
    platform: 'Explore the platform',
    docEyebrow: 'The document',
    docTitle: 'What a BlueCMR DeCA looks like',
    docText: 'A delivery round with three deliveries in a single document (sample data), with the data required by article 6 of Order FOM/2861/2012.',
    docAlt: 'Full DeCA issued with BlueCMR, with three deliveries and sample data',
    notes: [
      { title: 'QR code', text: 'The inspector scans it and opens the document instantly from a stable HTTPS address.' },
      { title: 'Contractual shipper', text: 'Name, tax ID (NIF) and address of the party that contracts the transport.' },
      { title: 'Actual carrier', text: 'Name and tax ID (NIF) of the party that performs the transport.' },
      { title: 'Vehicle', text: 'Registration of the tractor unit and the trailer. If it changes on the road, it is updated with the same URL and the same QR code.' },
      { title: 'Origin and date of transport', text: 'Place of loading and date on which the transport is performed.' },
      { title: 'Goods', text: 'Nature and weight of the goods.' },
      { title: 'Deliveries', text: 'Each with its consignee, its goods and its outcome, in the same document.' },
      { title: 'History', text: 'Every delivery confirmation or change is recorded with date and time.' },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From the loading dock to the last delivery',
    howText: 'The DeCA (formerly DCA) is the control document of Order FOM/2861/2012 and must be on board in all carriage of goods by road within Spanish territory. This is how you manage it with BlueCMR:',
    steps: [
      { place: 'Office', title: 'Issue', text: 'In under two minutes from the web or the app, or automatically from your ERP or TMS through the API.' },
      { place: 'Driver', title: 'Sending', text: 'The driver receives it by email, WhatsApp, SMS or link, with nothing to install, with delivery and read receipts.' },
      { place: 'On the road', title: 'Inspection', text: 'The inspector scans the QR code and sees the document instantly; the driver can show it even without mobile coverage.' },
      { place: 'Destination', title: 'Deliveries', text: 'Each delivery can be confirmed in the DeCA itself, with its outcome and time.' },
      { place: 'Afterwards', title: 'Archive', text: 'The document is preserved with all its changes and confirmations, with the data structured for eFTI.' },
    ],
    growEyebrow: 'Beyond the DeCA',
    growTitle: 'Start with the DeCA, grow when you are ready',
    growText: 'With the same data and the same platform you can move on to documents with more functions, without changing tools.',
    grow: [
      { title: 'Consignment note + DeCA', text: 'Contract and control document in one, with signatures.', to: '/documentos/carta-porte' },
      { title: 'ADR transport document + DeCA', text: 'Dangerous goods, with the control document data.', to: '/documentos/transporte-adr' },
      { title: 'Delivery note + DeCA', text: 'The shipper’s delivery note, also serving as the control document.' },
      { title: 'eCMR', text: 'For international transport, with the same workflow.', to: '/documentos/ecmr' },
      { title: 'Integration with your ERP or TMS', text: 'DeCAs generated automatically from your system, through the API or by file.', to: '/interoperabilidad' },
    ],
    faqEyebrow: 'FAQ',
    faqTitle: 'Common questions about the DeCA',
    faq: [
      { q: 'Is it the same as the DCA or the control document?', a: 'Yes. DeCA (Documento electrónico de Control Administrativo) is the name given by the Resolution of 5 June 2026 to the control document of Order FOM/2861/2012, previously known as DCA or documento de control.' },
      { q: 'Who has to issue it?', a: 'The contractual shipper draws it up, and the actual carrier draws it up and carries it on board. Both are answerable for it existing and being correctly completed.' },
      { q: 'Does it have to be signed?', a: 'No. A signature is not a validity requirement for the DeCA. If it is signed, it must be an advanced or qualified electronic signature. With BlueCMR you can add an advanced biometric signature when the DeCA also serves as proof of delivery.' },
      { q: 'What if I subcontract the transport?', a: 'Whoever subcontracts becomes the contractual shipper of the new shipment: they appear as such in the DeCA and provide their details to the actual carrier.' },
      { q: 'Can I use a single DeCA for several deliveries?', a: 'Yes, when the deliveries share the same contractual shipper and actual carrier, identifying each shipment within the document, as allowed by the Resolution of 5 June 2026.' },
      { q: 'Can another document serve as the DeCA?', a: 'Yes. A delivery note or a consignment note containing all the control document data also fulfils that obligation (art. 2.2 of Order FOM/2861/2012). BlueCMR offers a consignment note, a delivery note and an ADR document with the DeCA data.' },
      { q: 'Does it apply to international transport?', a: 'No. International transport is documented under the conventions signed by Spain, such as the CMR or the eCMR. The DeCA is for transport within Spain: national transport and cabotage.' },
      { q: 'Are any transport operations exempt?', a: 'Yes: private transport on own account (transporte privado complementario), which must prove it meets art. 102.2 a), c) and d) of the LOTT (Spanish Road Transport Act), and small-parcel delivery.' },
      { q: 'What happens if it is not carried?', a: 'Not carrying it, or carrying it on paper or incomplete, is penalised under the LOTT penalty regime.' },
    ],
    lawTitle: 'Legal framework',
    law: [
      { title: 'Order FOM/2861/2012', text: 'Control document in road transport.', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2013-154' },
      { title: 'Law 9/2025 on Sustainable Mobility', text: 'Mandatory electronic format.', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545' },
      { title: 'Resolution of 5 June 2026', text: 'Technical requirements of the DeCA (BOE-A-2026-12784).', href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784' },
      { title: 'LOTT (Law 16/1987)', text: 'Spanish Road Transport Act: penalty regime (arts. 141 and 143).', href: 'https://www.boe.es/buscar/act.php?id=BOE-A-1987-17803' },
      { title: 'eIDAS Regulation (EU 910/2014)', text: 'Advanced electronic signature.', href: 'https://eur-lex.europa.eu/eli/reg/2014/910/oj' },
    ],
    closing: 'Comply with the DeCA without changing how you work',
    closingText: 'From the first document to your whole fleet, on the same platform.',
    breadcrumb: ['Home', 'Documents'],
  },
};

const DocumentoControlPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);
  const notes = c.notes.map((n, i) => ({ ...n, ...MARKS[lang][i] }));

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="article"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: 'https://www.bluecmr.com/documentos' },
          { name: 'DeCA', url: PAGE_URL[lang] }
        ]}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: c.heroTitle, description: c.seoDescription, url: PAGE_URL[lang] },
          faqSchema(c.faq)
        ]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          documentImage={{ src: DOC_IMG[lang].hero, alt: c.heroAlt }}
          primary={primaryCta}
          secondary={{ to: getLocalizedUrl('/plataforma'), label: c.platform }}
          compact
        >
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {c.heroChecks.map((item) => (
              <li key={item} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </PageHero>

        {/* Cómo funciona */}
        <section className="py-20 md:py-24 bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.howEyebrow} title={c.howTitle} text={c.howText} />
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-10">
              {c.steps.map((step, i) => (
                <li key={step.title} className="border-t-2 border-slate-900 pt-5">
                  <p className="flex items-baseline gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
                    <span className="tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <span>{step.place}</span>
                  </p>
                  <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* El DeCA real, anotado */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.docEyebrow} title={c.docTitle} text={c.docText} />
            <AnnotatedDocument
              src={DOC_IMG[lang].full}
              alt={c.docAlt}
              width="1100"
              height={DOC_IMG[lang].h}
              notes={notes}
            />
          </div>
        </section>

        {/* Empiece por el DeCA, crezca cuando quiera */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.growEyebrow} title={c.growTitle} text={c.growText} className="" />
            </div>
            <ul className="lg:col-span-7 border-b border-slate-200">
              {c.grow.map((g) => {
                const body = (
                  <>
                    <span>
                      <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{g.title}</span>
                      <span className="mt-1 block text-sm text-slate-600">{g.text}</span>
                    </span>
                    {g.to && <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />}
                  </>
                );
                return (
                  <li key={g.title} className="border-t border-slate-200">
                    {g.to
                      ? <Link to={getLocalizedUrl(g.to)} className="group flex items-start justify-between gap-4 py-5">{body}</Link>
                      : <div className="flex items-start justify-between gap-4 py-5">{body}</div>}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Preguntas frecuentes */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionIntro eyebrow={c.faqEyebrow} title={c.faqTitle} className="" />
            </div>
            <div className="lg:col-span-8">
              <FaqList items={c.faq} />
            </div>
          </div>
        </section>

        {/* Marco normativo y recursos (los artículos solo existen en español) */}
        <section className="py-16 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-x-16 gap-y-12">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{c.lawTitle}</h2>
              <ul className="border-b border-slate-200">
                {c.law.map((n) => (
                  <li key={n.title} className="border-t border-slate-200">
                    <a href={n.href} target="_blank" rel="noopener noreferrer" className="group flex items-start justify-between gap-4 py-4">
                      <span>
                        <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{n.title}</span>
                        <span className="mt-0.5 block text-sm text-slate-600">{n.text}</span>
                      </span>
                      <ExternalLink className="mt-1 w-4 h-4 flex-shrink-0 text-slate-400 group-hover:text-primary-700" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            {lang === 'es' && (
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{c.resTitle}</h2>
                <ul className="border-b border-slate-200">
                  {c.res.map((r) => (
                    <li key={r.to} className="border-t border-slate-200">
                      <Link to={r.to} className="group flex items-start justify-between gap-4 py-4">
                        <span>
                          <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{r.title}</span>
                          <span className="mt-0.5 block text-sm text-slate-600">{r.text}</span>
                        </span>
                        <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default DocumentoControlPage;
